import api from './client';

export type MarkingStatus = 'ready' | 'off_balance' | 'reserved' | 'sold' | 'written_off';

export interface MarkingCode {
  id: number;                    // внутренний ID, не используется как ключ
  marking_code: string;          // ← теперь это главный идентификатор
  raw_code: string;
  gtin: string | null;
  serial_number: string | null;
  catalog_id: number | null;
  size_id: number | null;
  product_id: string | null;
  product_name: string | null;
  status: MarkingStatus;
  product_type: 'piece' | 'bulk' | 'set' | 'unknown';
  total_volume: number | null;
  bulk_remaining_volume: number | null;
  last_document_id: string | null;
  order_id: number | null;
  order_number: string | null;
  stock_receipt_id: number | null;
  scanned_by: number | null;
  scanned_at: string;
  resolved_by: number | null;
  resolved_at: string | null;
  note: string | null;
  scan_image: string | null;
  brand: string | null;
  container_size: string | null;
  scanned_by_name: string | null;
  already_in_user_buffer?: boolean;
  // Причина отказа Нацкаталога (rev. 2026-10-16), только в буфере при непривязанном GTIN
  gtin_failure_kind?: 'transient' | 'deterministic' | null;
}

export interface GtinInfo {
  gtin: string;
  catalog_id: number | null;
  product_name: string | null;
  brand: string | null;
  size_id: number | null;
  container_size: string | null;
}

export interface ContainerSizePreset {
  size: string;
  count: number;
}

export interface CatalogSize {
  id: number;
  container_size: string;
}

export type GtinBindBody =
  | { catalog_id: number; size_id: number }
  | { catalog_id: number; container_size: string };

export interface GtinSuggestion {
  gtin: string;
  status: 'pending' | 'auto_bound' | 'needs_review' | 'failed';
  failure_kind: 'transient' | 'deterministic' | null;
  suggested_brand: string | null;
  suggested_model_name: string | null;
  suggested_container_size: string | null;
  suggested_product_type: string | null;
  suggested_catalog_id: number | null;
  suggested_size_id: number | null;
  ai_confidence: number | null;
}

export interface BufferCode extends MarkingCode {
  // буфер — записи user_marks; статус марки при этом 'ready' (или другой из словаря)
}

export interface MarkVolumes {
  marking_code: string;
  product_type: 'piece' | 'bulk' | 'set' | 'unknown';
  total_volume: number | null;
  reserved_volume: number | null;
  available_volume: number | null;
}

export interface GtinMarkItem {
  marking_code: string;
  product_name: string | null;
  status: string;
  received_at: string | null;
  total_volume: number | null;
  available_volume: number | null;        // свободно для документа: min(наш кэш, остаток 1С − резерв) + уже взятое этим документом
  reserved_volume: number | null;         // в резерве (наши непроведённые документы)
  onec_remaining_liters?: number | null;  // физический остаток по регистру 1С (л)
  already_in_document: boolean;
  selectable: boolean;
  // Марка с криптохвостом (rev. 2026-09-17): false → красный тег «без криптохвоста»
  has_full_code?: boolean;
}

export type ScanAction = 'sale' | 'receipt' | 'bulk' | 'written_off';

export interface ResolveBody {
  action: ScanAction;
  order_id?: number;
  order_number?: string | null;
  stock_receipt_id?: number;
}

export interface ResolveBulkResult {
  resolved: number;
  already?: string[];
  errors: { marking_code: string; detail: string; status: number }[];
}

export const markingApi = {
  createScan: (raw_code: string, note?: string, scan_image?: string | null) =>
    api.post<MarkingCode>('/marking/scans', { raw_code, note, scan_image }),

  listScans: (skip = 0, limit = 50, status?: string, sortOrder: 'asc' | 'desc' = 'desc', search?: string, productType?: string) =>
    api.get<MarkingCode[]>('/marking/scans', {
      params: {
        skip,
        limit,
        sort_order: sortOrder,
        ...(status ? { status } : {}),
        ...(productType ? { product_type: productType } : {}),
        ...(search ? { q: search } : {}),
      },
    }),

  countScans: (status?: string, search?: string, productType?: string) =>
    api.get<{ total: number }>('/marking/scans/count', {
      params: {
        ...(status ? { status } : {}),
        ...(productType ? { product_type: productType } : {}),
        ...(search ? { q: search } : {}),
      },
    }),

  getScan: (id: number) =>
    api.get<MarkingCode>(`/marking/scans/${id}`),

  getCodeByMarkingCode: (marking_code: string) =>
    api.get<MarkingCode>(`/marking/codes/${encodeURIComponent(marking_code)}`),

  deleteScan: (id: number) =>
    api.delete(`/marking/scans/${id}`),

  getGtinInfo: (gtin: string) =>
    api.get<GtinInfo>(`/marking/gtin/${gtin}`),

  bindGtin: (gtin: string, body: GtinBindBody) =>
    api.post<GtinInfo>(`/marking/gtin/${gtin}/bind`, body),

  getContainerSizePresets: () =>
    api.get<ContainerSizePreset[]>('/marking/container-sizes/presets'),

  getCatalogSizes: (catalogId: number) =>
    api.get<CatalogSize[]>(`/product-catalog/${catalogId}/sizes`),

  suggestGtin: (gtin: string, force = false) =>
    api.get<GtinSuggestion>(`/marking/gtin/${gtin}/suggest`, {
      params: force ? { force: 1 } : {},
    }),

  // Buffer
  getBuffer: (userIdOrAll?: number | 'all') =>
    api.get<BufferCode[]>('/marking/buffer', {
      params: userIdOrAll !== undefined ? { user_id: userIdOrAll } : {},
    }),

  getBufferCount: () =>
    api.get<{ count: number }>('/marking/buffer/count'),

  resolveCode: (marking_code: string, body: ResolveBody) =>
    api.post<BufferCode>(`/marking/codes/${encodeURIComponent(marking_code)}/resolve`, body),

  resolveCodesBulk: (marking_codes: string[], action: ScanAction, order_id?: number, order_number?: string | null) =>
    api.post<ResolveBulkResult>('/marking/codes/resolve-bulk', { marking_codes, action, order_id, order_number }),

  unbindCode: (marking_code: string) =>
    api.post<BufferCode>(`/marking/codes/${encodeURIComponent(marking_code)}/unbind`, {}),

  reopenCode: (marking_code: string) =>
    api.post<BufferCode>(`/marking/codes/${encodeURIComponent(marking_code)}/reopen`, {}),

  deleteCode: (marking_code: string) =>
    api.delete(`/marking/codes/${encodeURIComponent(marking_code)}`),

  removeFromBuffer: (marking_code: string) =>
    api.delete(`/marking/user-marks/${encodeURIComponent(marking_code).replace(/%2F/gi, '%252F')}`),

  printThermal: (markingCodes: string[]) =>
    api.post<{ task_id: number }>('/marking/print-thermal', { marking_codes: markingCodes }),

  checkCanAdd: (marking_code: string, order_id?: number) =>
    api.post<{ can_add: boolean; reason: string | null; checks: Record<string, any>; unreceived?: boolean }>('/marking/check', { marking_code, order_id }),

  getVolumes: (marking_code: string) =>
    api.get<MarkVolumes>(`/marking/codes/${encodeURIComponent(marking_code)}/volumes`),

  getMarksByProduct: (product_id: string, document_id?: string, service_order_id?: number, strict?: boolean) =>
    api.get<GtinMarkItem[]>(`/marking/product/${product_id}/marks`, {
      params: {
        ...(document_id ? { document_id } : {}),
        ...(service_order_id ? { service_order_id } : {}),
        ...(strict ? { strict: true } : {}),
      },
    }),

  revalidateBuffer: () =>
    api.post<{ checked: number; reset: number }>('/marking/buffer/revalidate'),

  generateDataMatrixSvg: async (raw_code: string, scale = 4) => {
    const response = await api.post('/marking/datamatrix.svg', { raw_code, scale }, {
      responseType: 'blob',
    });
    return URL.createObjectURL(response.data as Blob);
  },

  generateDataMatrixSvgText: async (raw_code: string, scale = 4) => {
    const response = await api.post('/marking/datamatrix.svg', { raw_code, scale }, {
      responseType: 'text',
    });
    return response.data as string;
  },
};

  // --- Legacy aliases for backward compatibility during migration ---
export type MarkingScan = MarkingCode;
export type BufferScan = BufferCode;
export const resolveScan = markingApi.resolveCode;
export const resolveScansBulk = markingApi.resolveCodesBulk;
export const unbindScan = markingApi.unbindCode;
// Не экспортируем deleteScan, чтобы избежать конфликта с markingApi.deleteScan
export const printThermal = markingApi.printThermal;
